import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { InstallationsActions } from './installations.actions';
import { InstallationsState } from './installations.state';
import { defaultPageSize } from './installations.model';
import { InstallationDetailsActions } from '../installation-details';

export const initInstallationsState: InstallationsState = {
  installations: [],
  lockedInstallations: [],
  deregister: {
    pending: false,
    success: false,
    error: false,
  },
  isData: false,
  loading: true,
  loaded: false,
  error: null,
  numberOfInstallationsToDisplay: null,
};

export const installationsReducer: ActionReducer<InstallationsState, Action> = createReducer(
  initInstallationsState,

  on(
    InstallationsActions.listFirstThousandInstallations,
    (): InstallationsState => ({
      ...initInstallationsState,
    })
  ),

  on(
    InstallationsActions.listFirstThousandInstallationsSuccess,
    (state, { installations }): InstallationsState => ({
      ...state,
      installations,
      error: null,
    })
  ),

  on(
    InstallationsActions.listFirstThousandInstallationsError,
    (state, { error }): InstallationsState => ({
      ...state,
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    InstallationsActions.listSecondThousandInstallations,
    (state): InstallationsState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    InstallationsActions.listSecondThousandInstallationsSuccess,
    (state, { installations }): InstallationsState => ({
      ...state,
      installations: [...state.installations, ...installations],
      error: null,
    })
  ),

  on(
    InstallationsActions.listSecondThousandInstallationsError,
    (state, { error }): InstallationsState => ({
      ...state,
      isData: state.installations.length > 0,
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    InstallationsActions.listThirdThousandInstallations,
    (state): InstallationsState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    InstallationsActions.listThirdThousandInstallationsSuccess,
    (state, { installations }): InstallationsState => ({
      ...state,
      installations: [...state.installations, ...installations],
      error: null,
    })
  ),

  on(
    InstallationsActions.listThirdThousandInstallationsError,
    (state, { error }): InstallationsState => ({
      ...state,
      isData: state.installations.length > 0,
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    InstallationsActions.listLockedInstallations,
    (state): InstallationsState => ({
      ...state,
      loading: true,
    })
  ),

  on(
    InstallationsActions.listLockedInstallationsSuccess,
    (state, { lockedInstallations }): InstallationsState => {
      if (lockedInstallations?.length) {
        state.installations.map((inst) => {
          const isColdSystem = lockedInstallations.some(
            (lockedInstallation) => inst.id === lockedInstallation.id
          );

          inst.isColdSystem = isColdSystem;
        });
      }

      return {
        ...state,
        lockedInstallations,
        isData: state.installations.length > 0,
        loading: false,
        loaded: true,
      };
    }
  ),

  on(
    InstallationsActions.listLockedInstallationsError,
    (state, { error }): InstallationsState => ({
      ...state,
      isData: state.installations.length > 0,
      loading: false,
      loaded: true,
      error,
    })
  ),

  on(
    InstallationsActions.removeInstallation,
    (state, { installationId }): InstallationsState => {
      const installationsNotToBeRemoved = state.installations.filter(
        (inst) => inst.id !== installationId
      );

      return {
        ...state,
        installations: installationsNotToBeRemoved,
        isData: installationsNotToBeRemoved.length > 0,
      };
    }
  ),

  on(
    InstallationDetailsActions.deregisterGatewaySuccess,
    (state, { installationId, gatewaySerial }): InstallationsState => {
      const gatewaysInstallationNotToBeDeregistered = state.installations.map((inst) => {
        if (inst.id === installationId) {
          return {
            ...inst,
            gateways: inst.gateways.filter((gw) => gw.serial !== gatewaySerial),
          };
        }

        return inst;
      });

      return {
        ...state,
        installations: gatewaysInstallationNotToBeDeregistered,
      };
    }
  ),

  on(
    InstallationsActions.deregisterInstallation,
    (state): InstallationsState => ({
      ...state,
      deregister: {
        pending: true,
        success: false,
        error: false,
      },
    })
  ),

  on(
    InstallationsActions.deregisterInstallationSuccess,
    (state, { installationId }): InstallationsState => {
      const installationsNotToBeDeregistered = state.installations.filter(
        (inst) => inst.id !== installationId
      );

      return {
        ...state,
        deregister: {
          pending: false,
          success: true,
          error: false,
        },
        installations: installationsNotToBeDeregistered,
        isData: installationsNotToBeDeregistered.length > 0,
      };
    }
  ),

  on(
    InstallationsActions.deregisterInstallationError,
    (state): InstallationsState => ({
      ...state,
      deregister: {
        pending: false,
        success: false,
        error: true,
      },
    })
  ),

  on(
    InstallationsActions.resetDeregisterState,
    (state): InstallationsState => ({
      ...state,
      deregister: {
        pending: false,
        success: false,
        error: false,
      },
    })
  ),

  on(
    InstallationsActions.liveUpdateAggregatedStatus,
    (state, { gatewaySerial, aggregatedStatus, lastChangedAt }): InstallationsState => ({
      ...state,
      installations: state.installations.map((installationObject) => ({
        ...installationObject,
        gateways: installationObject.gateways.map((gateway) => {
          if (gateway.serial === gatewaySerial) {
            return {
              ...gateway,
              aggregatedStatus,
              lastStatusChangedAt: lastChangedAt,
            };
          }

          return gateway;
        }),
      })),
    })
  ),

  on(
    InstallationsActions.fetchNumberOfDisplayedInstallations.success,
    (state, { numberOfInstallationsToDisplay }): InstallationsState => ({
      ...state,
      numberOfInstallationsToDisplay,
    })
  ),

  on(
    InstallationsActions.fetchNumberOfDisplayedInstallations.failed,
    (state): InstallationsState => ({
      ...state,
      // prevents from excessive calls
      numberOfInstallationsToDisplay: defaultPageSize,
    })
  ),

  on(
    InstallationsActions.setNumberOfDisplayedInstallations.success,
    (state, { numberOfInstallationsToDisplay }): InstallationsState => ({
      ...state,
      numberOfInstallationsToDisplay,
    })
  ),

  on(
    InstallationsActions.setNumberOfDisplayedInstallations.failed,
    (state, { previousNumberOfInstallationsToDisplay }): InstallationsState => ({
      ...state,
      numberOfInstallationsToDisplay: previousNumberOfInstallationsToDisplay,
    })
  )
);
